<script lang="ts">
import type { GetObservationSpecieListData } from "@carnet/api/backoffice";

export type ObservationSpecieFilters = Pick<
  NonNullable<GetObservationSpecieListData["query"]>,
  "mineOnly" | "withPhotoOnly" | "taxonIds[]" | "featureCodes[]" | "specieLevelCodes[]" | "specieId"
>;
</script>

<script setup lang="ts">
import { getSiteDetail, getObservationSpecieList, type ObservationSpecieResource } from "@carnet/api/backoffice";

const props = defineProps<{
  siteId: number | string;
  filters?: ObservationSpecieFilters;
}>();

const emit = defineEmits<{
  close: [];
}>();

const { t } = useI18n();
const { photosTo, detailTo } = useObservationSpecieLinks(() => props.siteId);

const popupRef = useTemplateRef<{ scrollEl: HTMLElement | null }>("popupRef");

const siteResult = ref<Awaited<ReturnType<typeof getSiteDetail<"$fetch">>>>();
const siteLoading = ref(true);

async function loadSite() {
  siteLoading.value = true;
  try {
    siteResult.value = await getSiteDetail({
      composable: "$fetch",
      path: { locale: appLocale(), id: String(props.siteId) },
      headers: { Accept: "application/json" },
    });
  } catch {
    siteResult.value = undefined;
  } finally {
    siteLoading.value = false;
  }
}

watch(() => props.siteId, loadSite, { immediate: true });

const site = computed(() => siteResult.value?.data);

const { pageStates, total, totalPages, firstPageStatus, generation, registerContainer, setTopRef, loadPage, retry } =
  useInfiniteList<ObservationSpecieResource>({
    getRoot: () => popupRef.value?.scrollEl ?? null,
    resetWhen: [() => props.siteId, () => props.filters],
    initialPageSize: 6,
    fetchPage: async ({ page, signal }) => {
      const res = await getObservationSpecieList({
        composable: "$fetch",
        signal,
        path: { locale: appLocale() },
        query: { ...props.filters, siteId: Number(props.siteId), page, size: 6, sort: "observations_desc" },
        headers: { Accept: "application/json" },
      });
      return unwrapList<ObservationSpecieResource>(res);
    },
  });

const loading = computed(() => siteLoading.value || firstPageStatus.value === "pending");
</script>

<template>
  <WebMapPopup ref="popupRef" :loading="loading" collapsible color="elevated" @close="emit('close')">
    <template #loading>
      <div class="space-y-2">
        <USkeleton class="h-7 w-3/4" />
        <USkeleton class="h-5 w-1/2" />
      </div>
    </template>
    <div>
      <h3 class="text-2xl font-semibold">{{ site?.name }}</h3>
      <p v-if="site?.nearbyCityLabel">{{ site.nearbyCityLabel }}</p>
      <p>{{ t("observations.speciesCount", total) }}</p>
    </div>
    <template #content>
      <WebInfiniteList
        :page-states="pageStates"
        :first-page-status="firstPageStatus"
        :total="total"
        :total-pages="totalPages"
        :generation="generation"
        :register-container="registerContainer"
        :set-top-ref="setTopRef"
        :tile-height-fallback="360"
        @load="loadPage"
        @retry="retry"
      >
        <template #skeleton>
          <div class="bg-white px-4 py-6"><AppTileObservationSpecieSkeleton /></div>
        </template>
        <template #default="{ item }">
          <div class="bg-white px-4 py-6">
            <AppTileObservationSpecie :specie="item" :photos-to="photosTo(item)" :detail-to="detailTo(item)" />
          </div>
        </template>
      </WebInfiniteList>
    </template>
  </WebMapPopup>
</template>
